import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Dianthus shipping calculator helpers
 */

function dianthusCleanCep(value) {
  return (value || '').replace(/\D/g, '').slice(0, 8);
}

function dianthusFormatCep(value) {
  const cep = dianthusCleanCep(value);
  return cep.length > 5 ? cep.slice(0, 5) + '-' + cep.slice(5) : cep;
}

function dianthusFormatMoney(rate) {
  const rawPrice = rate.price || rate.price_value || rate.price_in_cents || '';
  let value = Number(rawPrice);

  if (Number.isNaN(value)) {
    return String(rawPrice).replace('.', ',');
  }

  if (value > 1000) {
    value = value / 100;
  }

  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function dianthusRemoveAnchorHashIfNeeded() {
  if (!window.location.hash) return;

  try {
    window.history.replaceState(
      window.history.state,
      document.title,
      window.location.pathname + window.location.search
    );
  } catch (error) {
    console.warn('Dianthus: não foi possível remover a âncora da URL.', error);
  }
}

async function dianthusWaitForCartReady() {
  for (let i = 0; i < 6; i++) {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        const cart = await response.json();

        if (cart && typeof cart.item_count === 'number' && cart.item_count > 0) {
          return cart;
        }
      }
    } catch (error) {
      console.warn('Dianthus cart ready check failed:', error);
    }

    await new Promise((resolve) => setTimeout(resolve, 350));
  }

  return null;
}

async function dianthusFetchCepData(cep) {
  const response = await fetch('https://viacep.com.br/ws/' + cep + '/json/');

  if (!response.ok) {
    throw new Error('Erro ao consultar CEP');
  }

  const data = await response.json();

  if (data.erro) {
    throw new Error('CEP não encontrado');
  }

  return data;
}

function dianthusBuildShippingParams(cep, cepData) {
  const params = new URLSearchParams();

  params.append('shipping_address[country]', 'Brazil');
  params.append('shipping_address[province]', cepData.uf || 'RJ');
  params.append('shipping_address[city]', cepData.localidade || 'Rio de Janeiro');
  params.append('shipping_address[zip]', cep);

  return params;
}

async function dianthusPollShippingRates(ratesUrl) {
  for (let i = 0; i < 12; i++) {
    const response = await fetch(ratesUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (response.status === 202) {
      await new Promise((resolve) => setTimeout(resolve, 900));
      continue;
    }

    const text = await response.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : null;
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      console.warn('Dianthus shipping async error:', response.status, data || text);
      throw new Error('Erro ao buscar frete');
    }

    if (data && Array.isArray(data.shipping_rates)) {
      return data.shipping_rates;
    }

    await new Promise((resolve) => setTimeout(resolve, 900));
  }

  return [];
}

async function dianthusGetRatesWithPrepare(params) {
  const prepareUrl = '/cart/prepare_shipping_rates.json?' + params.toString();
  const ratesUrl = '/cart/async_shipping_rates.json?' + params.toString();

  const prepareResponse = await fetch(prepareUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!prepareResponse.ok && prepareResponse.status !== 202) {
    const text = await prepareResponse.text();
    console.warn('Dianthus shipping prepare error:', prepareResponse.status, text);
    throw new Error('Erro ao preparar frete');
  }

  return await dianthusPollShippingRates(ratesUrl);
}

async function dianthusGetRatesDirect(params) {
  const directUrl = '/cart/shipping_rates.json?' + params.toString();

  const response = await fetch(directUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch (error) {
    data = null;
  }

  if (!response.ok) {
    console.warn('Dianthus shipping direct error:', response.status, data || text);
    throw new Error('Erro no endpoint direto');
  }

  return data && Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
}

function dianthusRenderShippingRates(calculator, rates) {
  const message = calculator.querySelector('[data-dianthus-shipping-message]');
  const ratesBox = calculator.querySelector('[data-dianthus-shipping-rates]');

  if (!message || !ratesBox) return;

  ratesBox.innerHTML = '';

  if (!rates || !rates.length) {
    message.textContent = 'Não encontramos opções de frete para este CEP. Você poderá ver o frete no checkout.';
    return;
  }

  message.textContent = '';

  rates.forEach((rate) => {
    const name = rate.presentment_name || rate.name || rate.service_name || 'Frete';
    const price = dianthusFormatMoney(rate);

    let delivery = '';

    if (rate.delivery_days && rate.delivery_days.length) {
      const days = rate.delivery_days
        .map((day) => Number(day))
        .filter((day) => !Number.isNaN(day));

      if (days.length) {
        const minDay = Math.min.apply(null, days);
        const maxDay = Math.max.apply(null, days);

        if (minDay === maxDay) {
          delivery = minDay === 1 ? '1 dia útil' : minDay + ' dias úteis';
        } else {
          delivery = minDay + ' a ' + maxDay + ' dias úteis';
        }
      }
    }

    if (!delivery && rate.description) {
      delivery = rate.description;
    }

    if (!delivery && rate.delivery_date) {
      delivery = 'Entrega estimada';
    }

    const item = document.createElement('div');
    item.className = 'dianthus-shipping-rate';

    item.innerHTML =
      '<div>' +
      '<strong>' +
      name +
      '</strong>' +
      (delivery ? '<small>' + delivery + '</small>' : '') +
      '</div>' +
      '<span>' +
      price +
      '</span>';

    ratesBox.appendChild(item);
  });
}

async function dianthusCalculateShipping(calculator) {
  if (!calculator) return;

  dianthusRemoveAnchorHashIfNeeded();

  const input = calculator.querySelector('[data-dianthus-cep]');
  const button = calculator.querySelector('[data-dianthus-calculate-shipping]');
  const message = calculator.querySelector('[data-dianthus-shipping-message]');
  const ratesBox = calculator.querySelector('[data-dianthus-shipping-rates]');

  if (!input || !button || !message || !ratesBox) return;

  const cep = dianthusCleanCep(input.value);

  ratesBox.innerHTML = '';

  if (cep.length !== 8) {
    message.textContent = 'Digite um CEP válido com 8 números.';
    return;
  }

  input.value = dianthusFormatCep(cep);
  button.disabled = true;
  button.textContent = 'Calculando...';
  message.textContent = 'Preparando carrinho...';

  try {
    await dianthusWaitForCartReady();

    message.textContent = 'Consultando CEP...';

    const cepData = await dianthusFetchCepData(cep);
    const params = dianthusBuildShippingParams(cep, cepData);

    message.textContent = 'Buscando opções de entrega...';

    let rates = [];

    try {
      rates = await dianthusGetRatesWithPrepare(params);
    } catch (prepareError) {
      console.warn('Dianthus prepare flow failed. Trying direct endpoint.', prepareError);
      rates = await dianthusGetRatesDirect(params);
    }

    if (!rates || !rates.length) {
      await new Promise((resolve) => setTimeout(resolve, 800));

      try {
        rates = await dianthusGetRatesWithPrepare(params);
      } catch (retryError) {
        console.warn('Dianthus retry failed. Trying direct endpoint again.', retryError);
        rates = await dianthusGetRatesDirect(params);
      }
    }

    dianthusRenderShippingRates(calculator, rates);
  } catch (error) {
    console.error('Erro ao calcular frete:', error);
    message.textContent = 'Não foi possível calcular agora. Você poderá ver o frete no checkout.';
  } finally {
    button.disabled = false;
    button.textContent = 'Calcular';
  }
}

function dianthusBindShippingCalculatorEvents() {
  if (window.dianthusShippingCalculatorGlobalBound) return;
  window.dianthusShippingCalculatorGlobalBound = true;

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target.closest('[data-dianthus-cep]');
      if (!input) return;

      input.value = dianthusFormatCep(input.value);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const input = event.target.closest('[data-dianthus-cep]');
      if (!input) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();

        const calculator = input.closest('[data-dianthus-shipping-calculator]');
        if (calculator) {
          dianthusCalculateShipping(calculator);
        }
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-dianthus-calculate-shipping]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const calculator = button.closest('[data-dianthus-shipping-calculator]');
      if (calculator) {
        dianthusCalculateShipping(calculator);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const calculator = event.target.closest('[data-dianthus-shipping-calculator]');
      if (!calculator) return;

      event.preventDefault();
      event.stopPropagation();

      dianthusCalculateShipping(calculator);
    },
    true
  );
}

dianthusBindShippingCalculatorEvents();

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    /**
     * Dianthus adjustment:
     * The original theme pushed a mobile history state when the cart drawer opened
     * and called history.back() when it closed.
     * On mobile, that behavior can force the page back to the top after closing the cart.
     * We remove that history behavior so the customer stays at the same scroll position.
     */
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    dianthusRemoveAnchorHashIfNeeded();

    window.setTimeout(() => {
      dianthusBindShippingCalculatorEvents();
    }, 150);

    if (this.hasAttribute('auto-open')) {
      this.showDialog();

      window.setTimeout(() => {
        this.#updateStickyState();
        dianthusBindShippingCalculatorEvents();
      }, 350);

      window.setTimeout(() => {
        this.#updateStickyState();
        dianthusBindShippingCalculatorEvents();
      }, 900);
    }
  };

  open() {
    dianthusRemoveAnchorHashIfNeeded();

    this.showDialog();

    window.setTimeout(() => {
      this.#updateStickyState();
      dianthusBindShippingCalculatorEvents();
    }, 250);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}